import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { UserStateEntry, UserStateMap } from '../types/user-state';
import { useUserState } from '../query/UserProvider';
import { useUpdateUserStateItem } from '../query/user-state/useUpdateUserStateItem';
import { useDeleteUserStateItem } from '../query/user-state/useDeleteUserStateItem';
import { isUserStateConflictError, applyUserStateEntry } from '../query/user-state/userStateOptimisticQuery';
import { userStateQueryKey } from '../query/user-state/userStateQueryKeys';
import { mergeConflictEntryField } from './tracked-job-conflict-merge';
import { buildSavedRoleItemKey } from '../utils/user-state/saved-role-item-key';
import { normalizeUserStateVersion } from '../utils/user-state/normalize-user-state-version';
import type { WatchlistSeenSnapshot } from '../utils/user-state/watchlist-seen-snapshot';

/**
 * Watch/unwatch a role from the normalised-title taxonomy (SEEKER_HUB_PLAN.md
 * decision 4) — a watched role is a live subscription to new postings with that
 * title. Storage mechanics mirror useCompanyFollowActions exactly (guest-first,
 * synced to UserState when authenticated). The `locale` the title was watched in
 * travels with the item — normalisedTitle facet values are per-locale and only
 * match their own jobs index.
 *
 * TEC-404 Phase C: same treatment as `useCompanyFollowActions.ts` — the Redux
 * `userState.entries` dual-write was removed (no reader left it fed besides this hook itself),
 * relying on `updateUserStateItem`/`deleteUserStateItem`'s own optimistic-update lifecycle
 * instead. No manual `isAuthenticated` gate (the mutation hooks branch internally), and reuses
 * `tracked-job-conflict-merge.ts`'s `mergeConflictEntryField` for `updateRoleMeta`'s conflict
 * merge rather than reimplementing it — no conflict-handling logic of its own beyond that.
 */
export function useRoleFollowActions() {
  const queryClient = useQueryClient();
  const { data: userState } = useUserState();
  const updateUserStateItem = useUpdateUserStateItem();
  const deleteUserStateItem = useDeleteUserStateItem();

  const followRole = useCallback(
    async (roleTitle: string, locale: 'en' | 'fi' | 'sv'): Promise<boolean> => {
      const itemKey = buildSavedRoleItemKey(roleTitle);
      const followedAt = new Date().toISOString();
      const data = { roleTitle, locale, followedAt };

      const currentEntry = userState?.[itemKey] as UserStateEntry | undefined;
      const version = normalizeUserStateVersion(currentEntry?.version);
      try {
        await updateUserStateItem.mutateAsync({ itemKey, data, version });
        return true;
      } catch (error) {
        return isUserStateConflictError(error);
      }
    },
    [updateUserStateItem, userState]
  );

  const unfollowRole = useCallback(
    async (roleTitle: string): Promise<boolean> => {
      const itemKey = buildSavedRoleItemKey(roleTitle);
      const previous = userState?.[itemKey] as UserStateEntry | undefined;

      try {
        await deleteUserStateItem.mutateAsync({ itemKey, version: previous?.version });
        return true;
      } catch {
        return false;
      }
    },
    [deleteUserStateItem, userState]
  );

  /** Patches watchlistSeen onto an existing watched-role entry, preserving roleTitle/locale/followedAt. */
  const updateRoleMeta = useCallback(
    async (roleTitle: string, patch: { watchlistSeen?: WatchlistSeenSnapshot }): Promise<boolean> => {
      const itemKey = buildSavedRoleItemKey(roleTitle);
      const currentEntry = userState?.[itemKey] as UserStateEntry | undefined;
      const currentData = (currentEntry?.data as Record<string, unknown> | undefined) ?? { roleTitle };
      const data = {
        ...currentData,
        roleTitle,
        ...(patch.watchlistSeen !== undefined ? { watchlistSeen: patch.watchlistSeen } : {}),
      };

      const version = normalizeUserStateVersion(currentEntry?.version);
      try {
        await updateUserStateItem.mutateAsync({ itemKey, data, version });
        return true;
      } catch (error) {
        if (isUserStateConflictError(error)) {
          const merged = mergeConflictEntryField(error.data, data);
          queryClient.setQueryData<UserStateMap>(userStateQueryKey, (current) =>
            applyUserStateEntry(current ?? {}, itemKey, merged)
          );
          return true;
        }
        return false;
      }
    },
    [queryClient, updateUserStateItem, userState]
  );

  return { followRole, unfollowRole, updateRoleMeta };
}
